import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Wallet, ArrowDownCircle, ArrowUpCircle } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";
const EMPTY = { kind: "income", account_id: "", category: "", amount: "", date: "", note: "", payment_mode: "" };

export default function Cashflow() {
  const { user } = useAuth();
  const [tab, setTab] = useState("income");
  const [flows, setFlows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/cashflow?kind=${tab}`).then((r) => setFlows(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [tab]);

  const openEdit = (f) => {
    setEditId(f.flow_id);
    setTab(f.kind);
    setForm({ kind: f.kind, account_id: f.account_id, category: f.category, amount: f.amount, date: f.date, note: f.note || "", payment_mode: f.payment_mode || "" });
    setCustom(f.custom || {});
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_id) return toast.error("Select a bank account");
    setBusy(true);
    try {
      const payload = { ...form, kind: editId ? form.kind : tab, amount: Number(form.amount), date: form.date || undefined, custom };
      if (editId) {
        await api.put(`/cashflow/${editId}`, payload);
        toast.success("Entry updated — bank balance recalculated");
      } else {
        await api.post("/cashflow", payload);
        toast.success(`${tab === "income" ? "Income" : "Expense"} recorded — bank balance updated`);
      }
      setOpen(false); setForm(EMPTY); setCustom({}); setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save entry");
    } finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/cashflow/${f.flow_id}`);
      toast.success("Entry deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const total = flows.reduce((s, f) => s + f.amount, 0);

  return (
    <div data-testid="portal-income-expenditure">
      <PageHeader title="Income & Expenditure" sub="Daily books — every entry hits the selected bank account balance">
        <Button onClick={() => { setForm(EMPTY); setEditId(null); setCustom({}); setOpen(true); }} data-testid="add-cashflow-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Add {tab === "income" ? "Income" : "Expense"}
        </Button>
      </PageHeader>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex rounded-full border border-slate-300 overflow-hidden" data-testid="cashflow-tabs">
          {["income", "expense"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`cashflow-tab-${t}`}
              className={`px-6 py-2 text-sm font-semibold capitalize transition-colors duration-200 ${tab === t ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
              {t}
            </button>
          ))}
        </div>
        <span className="font-mono2 text-sm text-slate-600" data-testid="cashflow-total">
          Total: <span className={tab === "income" ? "text-emerald-600 font-bold" : "text-red-600 font-bold"}>{inr(total)}</span>
        </span>
      </div>

      {flows.length === 0 ? (
        <EmptyState icon={Wallet} title={`No ${tab} entries`} sub="Record daily income & expenditure here" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]" data-testid="cashflow-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Date</th><th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Account</th><th className="px-5 py-3">Mode</th><th className="px-5 py-3">Note</th>
                <th className="px-5 py-3 text-right">Amount</th>
                {user?.role === "admin" && <th className="px-5 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {flows.map((f) => (
                <tr key={f.flow_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`cashflow-row-${f.flow_id}`}>
                  <td className="px-5 py-3.5 text-slate-600">{fdate(f.date)}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-900">
                    {f.source === "payment" ? (
                      <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 font-semibold ${f.kind === "income" ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-red-500/40 text-red-600 bg-red-500/10"}`} data-testid={`cashflow-payment-badge-${f.flow_id}`}>
                        {f.number}
                      </span>
                    ) : f.source === "purchase" ? (
                      <span className="px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-600 bg-amber-500/10 text-[11px] font-mono2 font-semibold" data-testid={`cashflow-purchase-badge-${f.flow_id}`}>
                        PURCHASE
                      </span>
                    ) : <span className="capitalize">{f.category}</span>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{f.account_name}</td>
                  <td className="px-5 py-3.5">
                    {f.payment_mode
                      ? <span className="px-2 py-0.5 rounded-full border border-blue-500/40 text-blue-600 bg-blue-500/10 text-[11px] font-medium">{f.payment_mode}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-500">{f.note || "—"}</td>
                  <td className={`px-5 py-3.5 text-right font-mono2 font-bold ${f.kind === "income" ? "text-emerald-600" : "text-red-600"}`}>
                    {f.kind === "income" ? "+" : "−"}{inr(f.amount)}
                  </td>
                  {user?.role === "admin" && (
                    <td className="px-5 py-3.5 text-right">
                      {f.source ? (
                        <span className="text-[11px] text-slate-400">via {f.source === "payment" ? "Payments" : f.source === "transport" ? "Transport" : "Purchases"} tab</span>
                      ) : (
                        <>
                          <button onClick={() => openEdit(f)} data-testid={`cashflow-edit-${f.flow_id}`}
                            className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button onClick={() => remove(f)} data-testid={`cashflow-delete-${f.flow_id}`}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); setCustom({}); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="add-cashflow-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {tab === "income" ? <ArrowDownCircle className="w-5 h-5 text-emerald-600" /> : <ArrowUpCircle className="w-5 h-5 text-red-600" />}
              {editId ? "Edit" : "Record"} {tab === "income" ? "Income" : "Expense"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Category *</Label>
              <Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder={tab === "income" ? "e.g. Service income, Subsidy" : "e.g. Freight, Salary, Rent"}
                data-testid="cashflow-category-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Amount (₹) *</Label>
                <Input type="number" step="0.01" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cashflow-amount-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cashflow-date-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Bank Account *</Label>
              <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="cashflow-account-select" className={selCls}>
                <option value="">Select account…</option>
                {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.name} — {inr(b.balance)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Payment Mode *</Label>
              <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="cashflow-mode-select" className={selCls}>
                <option value="">Select mode…</option>
                {["Cash", "UPI", "Bank Transfer", "Cheque"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cashflow-note-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <DynamicFields entity={tab} values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
            <Button type="submit" disabled={busy} data-testid="cashflow-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
              {busy ? "Saving…" : editId ? "Save Changes" : `Record ${tab === "income" ? "Income" : "Expense"}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
